import { useState } from "react";
import { useTranslation } from "react-i18next";
import { QRCodeSVG } from "qrcode.react";
import { motion } from "framer-motion";
import { Copy, Check, Smartphone, Apple, Play, ArrowUpRight } from "lucide-react";
import { API } from "@/lib/api";

const PLAY_URL =
  process.env.REACT_APP_PLAY_STORE_URL ||
  "https://play.google.com/apps/internaltest/4701465358857330345";

function fireEvent(name, source, target) {
  try {
    const body = JSON.stringify({ name, source, target });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(
        `${API}/analytics/event`,
        new Blob([body], { type: "application/json" })
      );
    } else {
      fetch(`${API}/analytics/event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch (_) { /* ignore */ }
}

export default function QRInstall() {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(PLAY_URL);
    setCopied(true);
    fireEvent("qr_copy", "qr-install", PLAY_URL);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <section className="py-24 md:py-32 bg-forest text-canvas relative noise overflow-hidden" data-testid="qr-install">
      {/* Ambient neon glow */}
      <div className="pointer-events-none absolute -left-20 top-1/3 w-[520px] h-[520px] rounded-full bg-lime/10 blur-[110px]" />
      <div className="pointer-events-none absolute -right-40 -bottom-32 w-[500px] h-[500px] rounded-full bg-forest-light/25 blur-[120px]" />

      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center relative">
        {/* Left copy */}
        <div className="lg:col-span-6">
          <div className="label-eyebrow !text-lime">{t("home.qrInstall.eyebrow")}</div>
          <h2 className="mt-4 font-display font-medium text-canvas text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tightest">
            Point a phone.<br />
            <span className="italic font-serif text-lime">Start sending.</span>
          </h2>
          <p className="mt-6 max-w-lg text-canvas/75 leading-relaxed">
            One code, one install. Perfect for founder meetings, press decks, and coffee-shop demos — no typing, no fumbling with URLs.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <a
              href={PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => fireEvent("qr_click_play", "qr-install", PLAY_URL)}
              className="btn-accent"
              data-testid="qr-install-play-cta"
            >
              <Play className="w-4 h-4" fill="currentColor" strokeWidth={0} />
              Open Play Store
              <ArrowUpRight className="w-4 h-4" />
            </a>
            <button
              type="button"
              onClick={copy}
              className="inline-flex items-center gap-2 px-4 py-3 rounded-full border border-canvas/25 text-canvas hover:bg-canvas/10 transition-colors"
              data-testid="qr-install-copy"
            >
              {copied ? <Check className="w-4 h-4 text-lime" /> : <Copy className="w-4 h-4" />}
              {copied ? "Link copied" : "Copy install link"}
            </button>
          </div>

          <div className="mt-10 grid grid-cols-2 max-w-sm gap-6">
            <Kpi kpi="Android" label="Internal Beta · live" icon={Play} live />
            <Kpi kpi="iOS" label="TestFlight · next" icon={Apple} />
          </div>
        </div>

        {/* Right — Big neon QR */}
        <div className="lg:col-span-6 flex justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, rotate: -3 }}
            whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
            className="relative"
            data-testid="qr-install-code"
          >
            {/* Outer neon frame */}
            <div className="relative p-6 md:p-8 rounded-[36px] bg-lime shadow-[0_50px_120px_-30px_rgba(232,255,0,0.5),0_20px_60px_-20px_rgba(45,13,94,0.6)]">
              <div className="rounded-[24px] bg-forest p-6 md:p-8 relative overflow-hidden">
                <div className="pointer-events-none absolute -right-10 -top-10 w-40 h-40 rounded-full bg-lime/25 blur-2xl" />
                <div className="flex items-center justify-between mb-6">
                  <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-lime">
                    Instantxfer CashApp
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-canvas/60">
                    <span className="live-dot" /> live
                  </span>
                </div>
                <div className="bg-lime rounded-2xl p-4 md:p-5">
                  <QRCodeSVG
                    value={PLAY_URL}
                    size={280}
                    bgColor="#E8FF00"
                    fgColor="#1A0B3D"
                    level="H"
                    includeMargin={false}
                    className="w-full h-auto max-w-[280px] mx-auto block"
                  />
                </div>
                <div className="mt-5 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-lime text-forest grid place-items-center shrink-0">
                    <Smartphone className="w-4 h-4" strokeWidth={2} />
                  </div>
                  <div className="text-canvas">
                    <div className="font-display text-lg tracking-tight">Scan with your phone</div>
                    <div className="text-canvas/60 text-xs">Opens Google Play internal test</div>
                  </div>
                </div>
              </div>
            </div>
            {/* Corner tag */}
            <div className="absolute -top-3 -right-3 md:-top-4 md:-right-4 bg-forest text-lime text-[10px] tracking-[0.24em] uppercase font-bold px-3 py-1.5 rounded-full ring-2 ring-lime">
              Beta · v0.9
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Kpi({ kpi, label, icon: Icon, live }) {
  return (
    <div className="flex items-start gap-3">
      <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${live ? "bg-lime text-ink" : "bg-canvas/[0.06] text-canvas border border-canvas/15"}`}>
        <Icon className="w-4 h-4" strokeWidth={2} fill={live ? "currentColor" : "none"} />
      </div>
      <div>
        <div className="font-display text-xl text-canvas tracking-tight">{kpi}</div>
        <div className="text-[10px] tracking-[0.22em] uppercase text-canvas/55">{label}</div>
      </div>
    </div>
  );
}
